import { useState, useEffect, useRef } from "react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { StatusBadge, formatDate } from "../components/StatusBadge";
import { toast } from "sonner";
import {
  getDateWiseReport, getCourseWiseReport, getYearWiseReport,
  getRejectionReport, getParentCallReport, getDailyPassesReport,
  exportReportExcel, getApprovalTimelineReport
} from "../lib/api";
import { 
  Calendar, GraduationCap, XCircle, Phone, Loader2, 
  Download, BarChart3, Search, Printer, ClipboardList,
  CheckCircle2, Clock, ShieldCheck, AlertTriangle, FileSpreadsheet,
  History
} from "lucide-react";

const ReportsModule = () => {
  const [activeReport, setActiveReport] = useState("today-passes");

  return (
    <div className="space-y-6" data-testid="reports-module">
      <Tabs value={activeReport} onValueChange={setActiveReport}>
        <TabsList className="flex flex-wrap h-auto gap-1">
          <TabsTrigger value="today-passes" data-testid="today-passes-tab" className="text-xs px-2">
            <ClipboardList className="h-3 w-3 mr-1" />
            Today
          </TabsTrigger>
          <TabsTrigger value="past-passes" data-testid="past-passes-tab" className="text-xs px-2">
            <Calendar className="h-3 w-3 mr-1" />
            Past Date
          </TabsTrigger>
          <TabsTrigger value="date-wise" data-testid="date-wise-tab" className="text-xs px-2">
            <BarChart3 className="h-3 w-3 mr-1" />
            Date-wise
          </TabsTrigger>
          <TabsTrigger value="course-wise" data-testid="course-wise-tab" className="text-xs px-2">
            <GraduationCap className="h-3 w-3 mr-1" />
            Course
          </TabsTrigger>
          <TabsTrigger value="year-wise" data-testid="year-wise-tab" className="text-xs px-2">
            <BarChart3 className="h-3 w-3 mr-1" />
            Year
          </TabsTrigger>
          <TabsTrigger value="rejections" data-testid="rejections-tab" className="text-xs px-2">
            <XCircle className="h-3 w-3 mr-1" />
            Rejections
          </TabsTrigger>
          <TabsTrigger value="parent-calls" data-testid="parent-calls-tab" className="text-xs px-2">
            <Phone className="h-3 w-3 mr-1" />
            Parent Calls
          </TabsTrigger>
          <TabsTrigger value="approval-timeline" data-testid="approval-timeline-tab" className="text-xs px-2">
            <History className="h-3 w-3 mr-1" />
            Approval Timeline
          </TabsTrigger>
        </TabsList>

        <TabsContent value="today-passes"><DailyPassReport isToday={true} /></TabsContent>
        <TabsContent value="past-passes"><DailyPassReport isToday={false} /></TabsContent>
        <TabsContent value="date-wise"><DateWiseReport /></TabsContent>
        <TabsContent value="course-wise"><CourseWiseReport /></TabsContent>
        <TabsContent value="year-wise"><YearWiseReport /></TabsContent>
        <TabsContent value="rejections"><RejectionReport /></TabsContent>
        <TabsContent value="parent-calls"><ParentCallReport /></TabsContent>
        <TabsContent value="approval-timeline"><ApprovalTimelineReport /></TabsContent>
      </Tabs>
    </div>
  );
};

/* ========== TODAY / PAST DATE PASSES ========== */
const DailyPassReport = ({ isToday }) => {
  const today = new Date().toISOString().split("T")[0];
  const [selectedDate, setSelectedDate] = useState(isToday ? today : "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const printRef = useRef(null);

  useEffect(() => {
    if (isToday) loadReport(today);
  }, []);

  const loadReport = async (date) => {
    if (!date) { toast.error("Please select a date"); return; }
    setLoading(true);
    try {
      const { data: res } = await getDailyPassesReport(date);
      setData(res);
    } catch (error) {
      toast.error("Failed to load report");
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    const content = printRef.current;
    if (!content) return;
    const win = window.open("", "_blank");
    win.document.write(`<!DOCTYPE html><html><head><title>Gate Pass Report - ${data?.date || selectedDate}</title>
    <style>
      * { margin:0; padding:0; box-sizing:border-box; }
      body { font-family: Arial, sans-serif; padding: 20px; font-size: 12px; }
      .header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #333; padding-bottom: 10px; }
      .header h1 { font-size: 18px; margin-bottom: 4px; }
      .header h2 { font-size: 14px; font-weight: normal; color: #555; }
      .summary { display: flex; justify-content: space-around; margin: 15px 0; padding: 10px; background: #f5f5f5; border-radius: 4px; }
      .summary-item { text-align: center; }
      .summary-item .num { font-size: 20px; font-weight: bold; }
      .summary-item .label { font-size: 10px; color: #666; text-transform: uppercase; }
      table { width: 100%; border-collapse: collapse; margin-top: 15px; }
      th, td { border: 1px solid #ccc; padding: 6px 8px; text-align: left; font-size: 11px; }
      th { background: #333; color: #fff; font-weight: 600; }
      tr:nth-child(even) { background: #f9f9f9; }
      .status-approved { color: #16a34a; font-weight: bold; }
      .status-rejected { color: #dc2626; font-weight: bold; }
      .status-pending { color: #d97706; font-weight: bold; }
      .status-verified { color: #2563eb; font-weight: bold; }
      .footer { margin-top: 20px; text-align: center; font-size: 10px; color: #999; border-top: 1px solid #ddd; padding-top: 10px; }
      @media print { body { padding: 10px; } }
    </style></head><body>`);
    win.document.write(content.innerHTML);
    win.document.write(`<div class="footer">Printed on ${new Date().toLocaleString()} | PIMS Gate Pass Management System</div>`);
    win.document.write("</body></html>");
    win.document.close();
    win.print();
  };

  const exportCSV = () => {
    if (!data?.passes?.length) return;
    const headers = "S.No,Request No,Student Name,Father Name,Reg No,Course,Year,Pass Type,Reason,Destination,Exit Date,Exit Time,Return Date,Return Time,Status,Parent Call,Gate Verified\n";
    const rows = data.passes.map((p, i) =>
      `${i+1},"${p.request_number}","${p.student_name}","${p.father_name}","${p.registration_number}","${p.course_name}",${p.year},"${p.pass_type}","${p.reason}","${p.destination}","${p.exit_date}","${p.exit_time}","${p.expected_return_date}","${p.expected_return_time}","${p.status}","${p.parent_call_done}","${p.gate_verified}"`
    ).join("\n");
    const blob = new Blob([headers + rows], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `passes_${data.date}.csv`;
    a.click();
  };

  const exportExcel = async () => {
    try {
      const response = await exportReportExcel(selectedDate || data.date, selectedDate || data.date);
      const url = URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `passes_${data.date}.xlsx`;
      a.click();
      toast.success("Excel downloaded!");
    } catch {
      toast.error("Failed to export Excel");
    }
  };

  const getStatusClass = (status) => {
    if (status === "approved") return "status-approved";
    if (status?.startsWith("rejected")) return "status-rejected";
    if (status?.startsWith("pending")) return "status-pending";
    if (status === "gate_verified") return "status-verified";
    return "";
  };

  const formatStatus = (status) => {
    if (!status) return "-";
    return status.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
  };

  return (
    <Card className="border-slate-200">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">{isToday ? "Today's Gate Passes" : "Past Date Gate Passes"}</CardTitle>
          {data?.passes?.length > 0 && (
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={exportCSV} data-testid="export-daily-csv">
                <Download className="h-4 w-4 mr-1" /> CSV
              </Button>
              <Button variant="outline" size="sm" onClick={exportExcel} data-testid="export-daily-excel" className="text-emerald-700 border-emerald-300 hover:bg-emerald-50">
                <FileSpreadsheet className="h-4 w-4 mr-1" /> Excel
              </Button>
              <Button size="sm" onClick={handlePrint} data-testid="print-daily-report">
                <Printer className="h-4 w-4 mr-1" /> Print
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {!isToday && (
          <div className="flex items-end gap-3">
            <div className="space-y-2">
              <Label>Select Date</Label>
              <Input
                type="date"
                value={selectedDate}
                max={today}
                onChange={(e) => setSelectedDate(e.target.value)}
                data-testid="past-date-input"
                className="w-48"
              />
            </div>
            <Button onClick={() => loadReport(selectedDate)} disabled={loading} data-testid="load-past-report">
              {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Search className="h-4 w-4 mr-1" />}
              View Passes
            </Button>
          </div>
        )}

        {loading && (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
          </div>
        )}

        {data && !loading && (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-5 gap-3">
              <SummaryCard icon={<ClipboardList className="h-5 w-5 text-slate-600" />} label="Total" value={data.summary.total} color="bg-slate-50 border-slate-200" />
              <SummaryCard icon={<Clock className="h-5 w-5 text-amber-600" />} label="Pending" value={data.summary.pending} color="bg-amber-50 border-amber-200" />
              <SummaryCard icon={<CheckCircle2 className="h-5 w-5 text-emerald-600" />} label="Approved" value={data.summary.approved} color="bg-emerald-50 border-emerald-200" />
              <SummaryCard icon={<XCircle className="h-5 w-5 text-red-600" />} label="Rejected" value={data.summary.rejected} color="bg-red-50 border-red-200" />
              <SummaryCard icon={<ShieldCheck className="h-5 w-5 text-blue-600" />} label="Gate Verified" value={data.summary.gate_verified} color="bg-blue-50 border-blue-200" />
              <SummaryCard icon={<CheckCircle2 className="h-5 w-5 text-slate-600" />} label="Closed" value={data.summary.closed || 0} color="bg-slate-50 border-slate-200" />
            </div>

            {/* Printable content (hidden, used for print) */}
            <div ref={printRef} className="hidden">
              <div className="header">
                <h1>PREM INSTITUTE OF MEDICAL SCIENCE (PIMS)</h1>
                <h2>Gate Pass Report - {formatDate(data.date)}</h2>
              </div>
              <div className="summary">
                <div className="summary-item"><div className="num">{data.summary.total}</div><div className="label">Total</div></div>
                <div className="summary-item"><div className="num">{data.summary.pending}</div><div className="label">Pending</div></div>
                <div className="summary-item"><div className="num">{data.summary.approved}</div><div className="label">Approved</div></div>
                <div className="summary-item"><div className="num">{data.summary.rejected}</div><div className="label">Rejected</div></div>
                <div className="summary-item"><div className="num">{data.summary.gate_verified}</div><div className="label">Gate Verified</div></div>
              </div>
              <table>
                <thead>
                  <tr>
                    <th>S.No</th>
                    <th>Request No.</th>
                    <th>Student Name</th>
                    <th>Father Name</th>
                    <th>Reg. No</th>
                    <th>Course/Year</th>
                    <th>Type</th>
                    <th>Reason</th>
                    <th>Destination</th>
                    <th>Exit Time</th>
                    <th>Return Date</th>
                    <th>Status</th>
                    <th>Parent Call</th>
                    <th>Gate</th>
                  </tr>
                </thead>
                <tbody>
                  {data.passes.map((p, i) => (
                    <tr key={p.id}>
                      <td>{i + 1}</td>
                      <td>{p.request_number}</td>
                      <td>{p.student_name}</td>
                      <td>{p.father_name}</td>
                      <td>{p.registration_number}</td>
                      <td>{p.course_name} - Y{p.year}</td>
                      <td style={{ textTransform: "capitalize" }}>{p.pass_type?.replace(/_/g, " ")}</td>
                      <td>{p.reason}</td>
                      <td>{p.destination}</td>
                      <td>{p.exit_time}</td>
                      <td>{p.expected_return_date}</td>
                      <td className={getStatusClass(p.status)}>{formatStatus(p.status)}</td>
                      <td>{p.parent_call_done}</td>
                      <td>{p.gate_verified}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* On-screen Table */}
            {data.passes.length > 0 ? (
              <div className="border rounded-lg overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead className="w-12">S.No</TableHead>
                      <TableHead>Request No.</TableHead>
                      <TableHead>Student Name</TableHead>
                      <TableHead>Father Name</TableHead>
                      <TableHead>Reg. No</TableHead>
                      <TableHead>Course/Year</TableHead>
                      <TableHead>Pass Type</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead>Destination</TableHead>
                      <TableHead>Exit Time</TableHead>
                      <TableHead>Return Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Parent Call</TableHead>
                      <TableHead>Gate</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.passes.map((p, i) => (
                      <TableRow key={p.id} data-testid={`daily-pass-row-${i}`}>
                        <TableCell className="font-medium">{i + 1}</TableCell>
                        <TableCell className="font-mono text-xs">{p.request_number}</TableCell>
                        <TableCell className="font-medium">{p.student_name}</TableCell>
                        <TableCell>{p.father_name || "-"}</TableCell>
                        <TableCell className="text-xs">{p.registration_number || "-"}</TableCell>
                        <TableCell className="whitespace-nowrap">{p.course_name} - Y{p.year}</TableCell>
                        <TableCell className="capitalize text-xs">{p.pass_type?.replace(/_/g, " ")}</TableCell>
                        <TableCell className="max-w-[150px] truncate text-xs">{p.reason}</TableCell>
                        <TableCell className="text-xs">{p.destination}</TableCell>
                        <TableCell className="whitespace-nowrap">{p.exit_time}</TableCell>
                        <TableCell className="whitespace-nowrap text-xs">{p.expected_return_date || "-"}</TableCell>
                        <TableCell><StatusBadge status={p.status} /></TableCell>
                        <TableCell>
                          {p.parent_call_done === "Yes" ? (
                            <Badge className="bg-emerald-100 text-emerald-700 text-xs">Yes</Badge>
                          ) : (
                            <Badge variant="outline" className="text-xs">No</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {p.gate_verified === "Yes" ? (
                            <Badge className="bg-blue-100 text-blue-700 text-xs">Verified</Badge>
                          ) : (
                            <Badge variant="outline" className="text-xs">-</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-12">
                <AlertTriangle className="h-10 w-10 mx-auto text-slate-300 mb-3" />
                <p className="text-slate-500 font-medium">No gate passes found for {formatDate(data.date)}</p>
              </div>
            )}
          </>
        )}

        {!data && !loading && isToday && (
          <div className="text-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-slate-300 mx-auto" />
            <p className="text-slate-400 mt-2">Loading today's passes...</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const SummaryCard = ({ icon, label, value, color }) => (
  <div className={`${color} border rounded-lg p-3 flex items-center gap-3`} data-testid={`summary-${label.toLowerCase()}`}>
    {icon}
    <div>
      <p className="text-2xl font-bold leading-none">{value}</p>
      <p className="text-xs text-slate-500 mt-1">{label}</p>
    </div>
  </div>
);

/* ========== DATE-WISE SUMMARY ========== */
const DateWiseReport = () => {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    const weekAgo = new Date(Date.now() - 7 * 86400000).toISOString().split("T")[0];
    setStartDate(weekAgo);
    setEndDate(today);
  }, []);

  const loadReport = async () => {
    if (!startDate || !endDate) { toast.error("Select date range"); return; }
    setLoading(true);
    try {
      const { data: res } = await getDateWiseReport(startDate, endDate);
      setData(res);
    } catch (error) {
      toast.error("Failed to load report");
    } finally {
      setLoading(false);
    }
  };

  const exportCSV = () => {
    if (!data?.details) return;
    const headers = "Request No,Student,Course,Year,Pass Type,Exit Date,Status\n";
    const rows = data.details.map(d => 
      `${d.request_number},${d.student_name},${d.course_name},${d.year},${d.pass_type},${d.exit_date},${d.status}`
    ).join("\n");
    const blob = new Blob([headers + rows], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `date_report_${startDate}_${endDate}.csv`; a.click();
  };

  const exportExcel = async () => {
    try {
      const response = await exportReportExcel(startDate, endDate);
      const url = URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `gate_pass_report_${startDate}_to_${endDate}.xlsx`;
      a.click();
      toast.success("Excel report downloaded!");
    } catch {
      toast.error("Failed to export Excel");
    }
  };

  return (
    <Card className="border-slate-200">
      <CardHeader><CardTitle>Date-wise Summary Report</CardTitle></CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-end gap-4">
          <div className="space-y-2">
            <Label>Start Date</Label>
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="start-date-input" />
          </div>
          <div className="space-y-2">
            <Label>End Date</Label>
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="end-date-input" />
          </div>
          <Button onClick={loadReport} disabled={loading} data-testid="generate-report-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Search className="h-4 w-4 mr-2" />}
            Generate
          </Button>
          {data?.details?.length > 0 && (
            <>
              <Button variant="outline" onClick={exportCSV} data-testid="export-csv-btn">
                <Download className="h-4 w-4 mr-2" /> CSV
              </Button>
              <Button variant="outline" onClick={exportExcel} data-testid="export-excel-btn" className="text-emerald-700 border-emerald-300 hover:bg-emerald-50">
                <FileSpreadsheet className="h-4 w-4 mr-2" /> Excel
              </Button>
            </>
          )}
        </div>
        {data && (
          <>
            {data.summary?.length > 0 && (
              <div className="border rounded-lg">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-center">Total</TableHead>
                      <TableHead className="text-center">Approved</TableHead>
                      <TableHead className="text-center">Rejected</TableHead>
                      <TableHead className="text-center">Pending</TableHead>
                      <TableHead className="text-center">Gate Verified</TableHead>
                      <TableHead className="text-center">Closed</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.summary.map((row) => (
                      <TableRow key={row._id}>
                        <TableCell className="font-medium">{formatDate(row._id)}</TableCell>
                        <TableCell className="text-center font-semibold">{row.total}</TableCell>
                        <TableCell className="text-center text-emerald-600">{row.approved}</TableCell>
                        <TableCell className="text-center text-red-600">{row.rejected}</TableCell>
                        <TableCell className="text-center text-amber-600">{row.pending}</TableCell>
                        <TableCell className="text-center text-blue-600">{row.gate_verified}</TableCell>
                        <TableCell className="text-center text-slate-600">{row.closed || 0}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
            {data.details?.length === 0 && <p className="text-center py-8 text-slate-500">No records found.</p>}
          </>
        )}
      </CardContent>
    </Card>
  );
};

/* ========== COURSE-WISE ========== */
const CourseWiseReport = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { loadReport(); }, []);
  const loadReport = async () => {
    try { const { data: res } = await getCourseWiseReport(); setData(res); }
    catch { toast.error("Failed to load report"); }
    finally { setLoading(false); }
  };
  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div>;
  return (
    <Card className="border-slate-200">
      <CardHeader><CardTitle>Course-wise Report</CardTitle></CardHeader>
      <CardContent>
        {data.length === 0 ? <p className="text-center py-8 text-slate-500">No data available.</p> : (
          <div className="border rounded-lg">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Course</TableHead>
                <TableHead className="text-center">Total</TableHead>
                <TableHead className="text-center">Approved</TableHead>
                <TableHead className="text-center">Rejected</TableHead>
                <TableHead className="text-center">Pending</TableHead>
                <TableHead className="text-center">Gate Verified</TableHead>
                <TableHead className="text-center">Closed</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {data.map((row) => (
                  <TableRow key={row.course_id} data-testid={`course-row-${row.course_id}`}>
                    <TableCell className="font-medium">{row.course_name}</TableCell>
                    <TableCell className="text-center font-semibold">{row.total}</TableCell>
                    <TableCell className="text-center text-emerald-600">{row.approved}</TableCell>
                    <TableCell className="text-center text-red-600">{row.rejected}</TableCell>
                    <TableCell className="text-center text-amber-600">{row.pending}</TableCell>
                    <TableCell className="text-center text-blue-600">{row.gate_verified}</TableCell>
                    <TableCell className="text-center text-slate-600">{row.closed || 0}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

/* ========== YEAR-WISE ========== */
const YearWiseReport = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { loadReport(); }, []);
  const loadReport = async () => {
    try { const { data: res } = await getYearWiseReport(); setData(res); }
    catch { toast.error("Failed to load report"); }
    finally { setLoading(false); }
  };
  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div>;
  return (
    <Card className="border-slate-200">
      <CardHeader><CardTitle>Year-wise Report</CardTitle></CardHeader>
      <CardContent>
        {data.length === 0 ? <p className="text-center py-8 text-slate-500">No data available.</p> : (
          <div className="border rounded-lg">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Course</TableHead>
                <TableHead>Year</TableHead>
                <TableHead className="text-center">Total</TableHead>
                <TableHead className="text-center">Approved</TableHead>
                <TableHead className="text-center">Rejected</TableHead>
                <TableHead className="text-center">Pending</TableHead>
                <TableHead className="text-center">Gate Verified</TableHead>
                <TableHead className="text-center">Closed</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {data.map((row, i) => (
                  <TableRow key={i} data-testid={`year-row-${row.course_id}-${row.year}`}>
                    <TableCell className="font-medium">{row.course_name}</TableCell>
                    <TableCell>Year {row.year}</TableCell>
                    <TableCell className="text-center font-semibold">{row.total}</TableCell>
                    <TableCell className="text-center text-emerald-600">{row.approved}</TableCell>
                    <TableCell className="text-center text-red-600">{row.rejected}</TableCell>
                    <TableCell className="text-center text-amber-600">{row.pending}</TableCell>
                    <TableCell className="text-center text-blue-600">{row.gate_verified}</TableCell>
                    <TableCell className="text-center text-slate-600">{row.closed || 0}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

/* ========== REJECTION REPORT ========== */
const RejectionReport = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { loadReport(); }, []);
  const loadReport = async () => {
    try { const { data: res } = await getRejectionReport(); setData(res); }
    catch { toast.error("Failed to load report"); }
    finally { setLoading(false); }
  };
  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div>;
  return (
    <Card className="border-slate-200">
      <CardHeader><CardTitle>Rejection Report</CardTitle></CardHeader>
      <CardContent>
        {data.length === 0 ? <p className="text-center py-8 text-slate-500">No rejected requests found.</p> : (
          <div className="border rounded-lg">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Request No.</TableHead>
                <TableHead>Student</TableHead>
                <TableHead>Course/Year</TableHead>
                <TableHead>Rejected By</TableHead>
                <TableHead>Stage</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead>Date</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {data.map((row, i) => (
                  <TableRow key={i} data-testid={`rejection-row-${i}`}>
                    <TableCell className="font-mono text-sm">{row.request_number}</TableCell>
                    <TableCell>{row.student_name}</TableCell>
                    <TableCell>{row.course_name} - Y{row.year}</TableCell>
                    <TableCell className="font-medium">{row.rejected_by}</TableCell>
                    <TableCell><Badge variant="outline" className="capitalize">{row.rejected_at_stage}</Badge></TableCell>
                    <TableCell className="max-w-[200px]"><p className="text-red-600 text-sm">{row.rejection_reason}</p></TableCell>
                    <TableCell>{formatDate(row.date)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

/* ========== PARENT CALL REPORT ========== */
const ParentCallReport = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { loadReport(); }, []);
  const loadReport = async () => {
    try { const { data: res } = await getParentCallReport(); setData(res); }
    catch { toast.error("Failed to load report"); }
    finally { setLoading(false); }
  };
  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div>;
  return (
    <Card className="border-slate-200">
      <CardHeader><CardTitle>Parent Call Report</CardTitle></CardHeader>
      <CardContent>
        {data.length === 0 ? <p className="text-center py-8 text-slate-500">No parent call records found.</p> : (
          <div className="border rounded-lg">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Request No.</TableHead>
                <TableHead>Student</TableHead>
                <TableHead>Course/Year</TableHead>
                <TableHead>Call Date/Time</TableHead>
                <TableHead>Who Received</TableHead>
                <TableHead>Relation</TableHead>
                <TableHead>Response</TableHead>
                <TableHead>Recorded By</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {data.map((row, i) => (
                  <TableRow key={i} data-testid={`parent-call-row-${i}`}>
                    <TableCell className="font-mono text-sm">{row.request_number}</TableCell>
                    <TableCell>{row.student_name}</TableCell>
                    <TableCell>{row.course_name} - Y{row.year}</TableCell>
                    <TableCell>{row.call_date} {row.call_time}</TableCell>
                    <TableCell>{row.who_received}</TableCell>
                    <TableCell>{row.relation}</TableCell>
                    <TableCell className="max-w-[200px] truncate">{row.parent_response}</TableCell>
                    <TableCell>{row.recorded_by}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

/* ========== APPROVAL TIMELINE ========== */
const ApprovalTimelineReport = () => {
  const today = new Date().toISOString().split("T")[0];
  const weekAgo = new Date(Date.now() - 7 * 86400000).toISOString().split("T")[0];
  const [startDate, setStartDate] = useState(weekAgo);
  const [endDate, setEndDate] = useState(today);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const loadReport = async () => {
    if (!startDate || !endDate) { toast.error("Select both dates"); return; }
    setLoading(true);
    try {
      const { data: result } = await getApprovalTimelineReport(startDate, endDate);
      setData(result);
    } catch { toast.error("Failed to load report"); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadReport(); }, []);

  const fmtTime = (ts) => {
    if (!ts) return "-";
    try {
      const d = new Date(ts);
      return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) + " " + d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
    } catch { return ts; }
  };

  const actionColor = (action) => {
    if (action === "approve" || action === "approved" || action === "verified" || action === "closed" || action === "created") return "text-emerald-700 bg-emerald-50";
    if (action === "reject" || action === "rejected") return "text-red-700 bg-red-50";
    return "text-slate-700 bg-slate-50";
  };

  return (
    <Card className="border-slate-200" data-testid="approval-timeline-report">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <History className="h-4 w-4" /> Approval Timeline Report
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-end gap-3 flex-wrap">
          <div className="space-y-1">
            <Label className="text-xs">From</Label>
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-40 h-8 text-xs" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">To</Label>
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="w-40 h-8 text-xs" />
          </div>
          <Button size="sm" className="h-8 text-xs" onClick={loadReport} disabled={loading} data-testid="load-timeline-btn">
            {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Search className="h-3 w-3 mr-1" />} Search
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : data.length === 0 ? (
          <p className="text-center py-8 text-slate-400 text-sm">No passes found for this date range.</p>
        ) : (
          <div className="space-y-3">
            <Badge variant="outline" className="text-xs">{data.length} passes found</Badge>
            <div className="border rounded-lg overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 text-xs">
                    <TableHead className="text-xs">Request No.</TableHead>
                    <TableHead className="text-xs">Student</TableHead>
                    <TableHead className="text-xs">Course/Yr</TableHead>
                    <TableHead className="text-xs">Created</TableHead>
                    <TableHead className="text-xs">Status</TableHead>
                    <TableHead className="text-xs">Timeline</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.map((row) => (
                    <TableRow key={row.id} className="text-xs align-top">
                      <TableCell className="font-mono text-xs py-2">{row.request_number}</TableCell>
                      <TableCell className="py-2">
                        <p className="font-medium text-xs">{row.student_name}</p>
                        <p className="text-[10px] text-slate-400">{row.student_enrollment}</p>
                      </TableCell>
                      <TableCell className="py-2 text-xs">{row.course_name}-Y{row.year}</TableCell>
                      <TableCell className="py-2 text-xs whitespace-nowrap">{fmtTime(row.created_at)}</TableCell>
                      <TableCell className="py-2"><StatusBadge status={row.status} /></TableCell>
                      <TableCell className="py-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-2 text-xs"
                          onClick={() => setExpandedId(expandedId === row.id ? null : row.id)}
                          data-testid={`expand-timeline-${row.id}`}
                        >
                          <History className="h-3 w-3 mr-1" />
                          {expandedId === row.id ? "Hide" : "View"} ({row.timeline?.length || 0} steps)
                        </Button>
                        {expandedId === row.id && row.timeline && (
                          <div className="mt-2 space-y-1.5 border-l-2 border-slate-200 pl-3 ml-1" data-testid={`timeline-detail-${row.id}`}>
                            {row.timeline.map((step, i) => (
                              <div key={i} className="relative">
                                <div className="absolute -left-[19px] top-1 w-2.5 h-2.5 rounded-full bg-slate-300 border-2 border-white" />
                                <div className={`p-2 rounded text-xs ${actionColor(step.action)}`}>
                                  <div className="flex justify-between items-start gap-2">
                                    <div>
                                      <p className="font-semibold">{step.step}</p>
                                      <p className="text-[10px] opacity-75">{step.actor}</p>
                                      {step.remarks && <p className="text-[10px] mt-0.5 italic">"{step.remarks}"</p>}
                                    </div>
                                    <p className="text-[10px] whitespace-nowrap font-mono">{fmtTime(step.timestamp)}</p>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ReportsModule;
